import { useEffect, useState } from 'react';
import { api } from '../services/api.js';

/**
 * Sticky action bar for batch reassign. Appears when ≥1 item is selected.
 * Stages a single set of changes and applies them in one batch call.
 *
 *   <BatchActionBar
 *     count={selectedIds.size}
 *     onApply={async (set) => { await api.batchUpdateItems([...selectedIds], set); ... }}
 *     onCancel={() => exit select mode}
 *   />
 *
 * The bar builds a `set` object matching the batch API:
 *   { category?, subcategory?, clearCategory?, addTags?, removeTags? }
 * Only the controls the user actually touches contribute to `set`.
 */
export default function BatchActionBar({ count, onApply, onCancel }) {
  const [taxonomy, setTaxonomy] = useState({ categories: {}, tags: [] });
  const [busy, setBusy] = useState(false);

  // Staged changes
  const [mode, setMode] = useState(null); // which action panel is open
  const [stagedCategory, setStagedCategory] = useState('');
  const [stagedSub, setStagedSub] = useState('');
  const [clearCat, setClearCat] = useState(false);
  const [addTags, setAddTags] = useState([]);
  const [removeTags, setRemoveTags] = useState([]);

  useEffect(() => {
    api.getCategories().then(setTaxonomy).catch(() => {});
  }, []);

  const categoryNames = Object.keys(taxonomy.categories);
  const subOptions = stagedCategory ? (taxonomy.categories[stagedCategory] ?? []) : [];

  // Build the set object from whatever was staged.
  function buildSet() {
    const set = {};
    if (clearCat) {
      set.clearCategory = true;
    } else {
      if (stagedCategory) {
        set.category = stagedCategory;
        if (stagedSub) set.subcategory = stagedSub;
      }
    }
    if (addTags.length) set.addTags = addTags;
    if (removeTags.length) set.removeTags = removeTags;
    return set;
  }

  const set = buildSet();
  const hasChanges = Object.keys(set).length > 0;

  async function apply() {
    if (!hasChanges) return;
    setBusy(true);
    try {
      await onApply(set);
    } finally {
      setBusy(false);
    }
  }

  function toggleAddTag(t) {
    setAddTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    // A tag can't be both added and removed.
    setRemoveTags((prev) => prev.filter((x) => x !== t));
  }
  function toggleRemoveTag(t) {
    setRemoveTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    setAddTags((prev) => prev.filter((x) => x !== t));
  }

  // Summary of what will happen, for the confirm affordance.
  const summaryParts = [];
  if (set.clearCategory) summaryParts.push('clear category');
  else if (set.category) summaryParts.push(`category → ${set.category}${set.subcategory ? ` › ${set.subcategory}` : ''}`);
  if (set.addTags) summaryParts.push(`+tags: ${set.addTags.join(', ')}`);
  if (set.removeTags) summaryParts.push(`−tags: ${set.removeTags.join(', ')}`);

  return (
    <div className="batch-bar" role="region" aria-label="Batch edit selected items">
      <div className="row between wrap">
        <span className="muted tiny fw-600">{count} selected</span>

        <div className="row wrap gap-6">
          <BarButton active={mode === 'category'} onClick={() => setMode(mode === 'category' ? null : 'category')}>Set category</BarButton>
          <BarButton active={mode === 'addtag'} onClick={() => setMode(mode === 'addtag' ? null : 'addtag')}>Add tag</BarButton>
          <BarButton active={mode === 'removetag'} onClick={() => setMode(mode === 'removetag' ? null : 'removetag')}>Remove tag</BarButton>
          <BarButton active={clearCat} onClick={() => { setClearCat((c) => !c); setStagedCategory(''); setStagedSub(''); setMode(null); }}>
            Clear category
          </BarButton>
        </div>

        <div className="row gap-8">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={apply} disabled={busy || !hasChanges}>
            {busy ? 'Applying…' : `Apply to ${count}`}
          </button>
        </div>
      </div>

      {/* Expandable panels */}
      {mode === 'category' && !clearCat && (
        <div className="row wrap gap-10 mt-12">
          <select value={stagedCategory} onChange={(e) => { setStagedCategory(e.target.value); setStagedSub(''); }}
            className="select-sm" aria-label="Category">
            <option value="">— choose category —</option>
            {categoryNames.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {stagedCategory && subOptions.length > 0 && (
            <select value={stagedSub} onChange={(e) => setStagedSub(e.target.value)} className="select-sm" aria-label="Subcategory">
              <option value="">— subcategory (optional) —</option>
              {subOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      )}

      {mode === 'addtag' && (
        <TagChooser tags={taxonomy.tags} selected={addTags} onToggle={toggleAddTag} accent="gold" />
      )}
      {mode === 'removetag' && (
        <TagChooser tags={taxonomy.tags} selected={removeTags} onToggle={toggleRemoveTag} accent="danger" />
      )}

      {hasChanges && (
        <div className="muted tiny mt-10">
          Will apply: {summaryParts.join('  ·  ')}
        </div>
      )}
    </div>
  );
}

function BarButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn toggle-btn${active ? ' is-on' : ''}`}
      aria-pressed={!!active}
    >
      {children}
    </button>
  );
}

function TagChooser({ tags, selected, onToggle, accent }) {
  const onClass = accent === 'danger' ? ' is-on is-danger' : ' is-on';
  return (
    <div className="chips mt-12">
      {tags.map((t) => {
        const on = selected.includes(t);
        return (
          <button key={t} type="button" onClick={() => onToggle(t)}
            aria-pressed={on}
            className={`chip${on ? onClass : ''}`}>
            {t}
          </button>
        );
      })}
    </div>
  );
}
